import Axios from "../config/axios-config";
import { apiErrorCode, apiErrorMessage } from "../utils/apiErrorMessage";

// ─────────────────────────────────────────────────────────────────────────────
// Novedades para comercios (microservice-contracts, `contracts/product-news`).
//
// Dos públicos:
//   - el portal del COMERCIO (campana del encabezado): `getNewsFeed`,
//     `getNewsUnreadCount` y `sendNewsReceipts`. Nunca deben romper ni demorar
//     el portal: timeout corto y, ante cualquier error, "sin novedades".
//   - el superadmin del sistema en /plataforma/novedades: el resto. Devuelven
//     `{ ok, data, error, code }` para que el editor muestre el motivo que da
//     el backend (`code` estable, `error` legible).
// ─────────────────────────────────────────────────────────────────────────────

const BASE = "contracts/product-news";
const PLATFORM = `${BASE}/platform`;

// El portal no espera más que esto: sin respuesta, se sigue sin novedades.
const PORTAL_TIMEOUT_MS = 5000;

async function platformCall(request, fallback) {
  try {
    const response = await request();
    return { ok: true, data: response.data, error: null, code: null };
  } catch (err) {
    return {
      ok: false,
      data: null,
      error: apiErrorMessage(err, fallback),
      code: apiErrorCode(err),
    };
  }
}

// ── Portal del comercio ──────────────────────────────────────────────────────

/**
 * Panel de novedades del comercio de la sesión.
 * @returns {Promise<{ brand, items, unread, total, page, limit } | null>} null ante error.
 */
export async function getNewsFeed({ page = 1, limit = 10 } = {}) {
  try {
    const { data } = await Axios.get(`${BASE}/feed`, {
      params: { page, limit },
      timeout: PORTAL_TIMEOUT_MS,
    });
    return data && Array.isArray(data.items) ? data : null;
  } catch {
    return null;
  }
}

const EMPTY_SUMMARY = { unread: 0, menuHighlights: [] };

/**
 * Lo que el portal pide al cargar: no leídas para el contador de la campana y
 * los ítems del menú que llevan la etiqueta "Nuevo".
 *
 * Ante error, nada: un badge falso es peor que ninguno.
 * @returns {Promise<{ unread: number, menuHighlights: { path: string, newsIds: string[] }[] }>}
 */
export async function getNewsSummary() {
  try {
    const { data } = await Axios.get(`${BASE}/unread-count`, { timeout: PORTAL_TIMEOUT_MS });
    const unread = Number(data?.unread);
    const menuHighlights = Array.isArray(data?.menuHighlights)
      ? data.menuHighlights.filter(
          (h) => typeof h?.path === "string" && Array.isArray(h?.newsIds) && h.newsIds.length > 0,
        )
      : [];
    return { unread: Number.isFinite(unread) && unread > 0 ? unread : 0, menuHighlights };
  } catch {
    return EMPTY_SUMMARY;
  }
}

/**
 * Registra eventos en lote. Best-effort: el backend es idempotente y un recibo
 * perdido solo afecta las métricas (o deja una novedad como no leída).
 * @param {{ id: string, event: "seen"|"opened"|"cta_clicked"|"visited" }[]} items
 */
export async function sendNewsReceipts(items) {
  if (!Array.isArray(items) || items.length === 0) return true;
  try {
    await Axios.post(`${BASE}/receipts`, { items: items.slice(0, 50) }, { timeout: PORTAL_TIMEOUT_MS });
    return true;
  } catch {
    return false;
  }
}

// ── /plataforma ──────────────────────────────────────────────────────────────

export const listProductNews = (params) =>
  platformCall(() => Axios.get(PLATFORM, { params }), "No se pudieron cargar las novedades.");

export const createProductNews = (body) =>
  platformCall(() => Axios.post(PLATFORM, body), "No se pudo crear la novedad.");

export const updateProductNews = (id, body) =>
  platformCall(() => Axios.patch(`${PLATFORM}/${id}`, body), "No se pudo guardar la novedad.");

export const publishProductNews = (id) =>
  platformCall(() => Axios.post(`${PLATFORM}/${id}/publish`), "No se pudo publicar la novedad.");

export const archiveProductNews = (id) =>
  platformCall(() => Axios.post(`${PLATFORM}/${id}/archive`), "No se pudo archivar la novedad.");

/** @param {{ audience: { countries?: string[], operatorIds?: string[], integrations?: string[] } }} body */
export const previewProductNewsAudience = (body) =>
  platformCall(
    () => Axios.post(`${PLATFORM}/audience-preview`, body),
    "No se pudo calcular el alcance.",
  );

export const getProductNewsAudienceOptions = () =>
  platformCall(
    () => Axios.get(`${PLATFORM}/audience-options`),
    "No se pudieron cargar los países y las integraciones.",
  );
